// Two-track state model for the Specific Claims redesign (Feature #5378).
//
// The two submission actions are independent (#5355 glossary):
//   • Aura funding track — "State of funds" (Accel/Prompt-Pay policies only).
//   • Carrier submission track — "Stop-loss claim to carrier".
//
// This module derives the per-track chip (label + color) and the policy-conditional
// "done" state from the read-model fields exposed by US 5574. Aura accents use the
// theme primary (teal); carrier uses secondary — per the #5378 planning palette
// (bind to theme tokens, not hardcoded hex, via StatusChip).
import type { StoplossClaim } from "@/types";
import type { StatusChipColor } from "@/components/common/fields/StatusChipField";
import { formatShortDate } from "@/utils/formatters/dateFormatter";

export interface TrackChip {
  label: string;
  color: StatusChipColor;
}

// Aura funding track state, derived from the read-model fields. "funded" once the
// money is disbursed (or Aura reports funded); "requested" once a funding request
// was POSTed (aura_submitted_at); "rejected" on an Aura 4xx; else "not_requested".
export type AuraTrackState = "not_requested" | "requested" | "funded" | "rejected";

export const auraTrackState = (claim: StoplossClaim): AuraTrackState => {
  if (claim.aura_disbursed_at || claim.aura_receivable_status === "funded") return "funded";
  if (claim.aura_receivable_status === "rejected" || claim.aura_receivable_status === "denied") return "rejected";
  if (claim.aura_submitted_at) return "requested";
  return "not_requested";
};

// Chip for the Aura funding column once the track has been actioned; null when
// not_requested (the caller renders the "Request Funding" action instead).
export const auraTrackChip = (claim: StoplossClaim): TrackChip | null => {
  switch (auraTrackState(claim)) {
    case "funded":
      return {
        label: claim.aura_disbursed_at ? `Funded ${formatShortDate(claim.aura_disbursed_at)}` : "Funded",
        color: "primary",
      };
    case "requested":
      return {
        label: claim.aura_submitted_at ? `Requested ${formatShortDate(claim.aura_submitted_at)}` : "Requested",
        color: "primary",
      };
    case "rejected":
      return { label: "Rejected", color: "error" };
    case "not_requested":
      return null;
  }
};

// Chip for the carrier column once submitted; null when not (caller renders the
// "Mark as Submitted" action instead).
export const carrierTrackChip = (claim: StoplossClaim): TrackChip | null =>
  claim.carrier_submitted_at
    ? { label: `Submitted ${formatShortDate(claim.carrier_submitted_at)}`, color: "secondary" }
    : null;

// Policy-conditional "done" (#5392): an Accel/Prompt-Pay policy requires BOTH
// tracks (Aura funded AND carrier-submitted); a non-Aura policy is done on
// carrier submission alone (the Aura track does not apply).
export const isTwoTrackComplete = (claim: StoplossClaim, policyAccelEnabled: boolean): boolean => {
  const carrierDone = Boolean(claim.carrier_submitted_at);
  if (!policyAccelEnabled) return carrierDone;
  return carrierDone && auraTrackState(claim) === "funded";
};
